import { formatLocaleDate } from '../../utils';

const ACTIVITY_RECORD_ID_KEYS = [
  'id',
  'record_id',
  'activity_record_id',
  'log_id',
];

const ACTIVITY_RECORD_TYPE_KEYS = ['type', 'action_type', 'action', 'event'];

const ACTIVITY_RECORD_TARGET_KEYS = [
  'target',
  'entity',
  'module',
  'subject',
  'object',
];

const ACTIVITY_RECORD_CHANGES_KEYS = [
  'summary',
  'changes',
  'change_summary',
  'description',
  'message',
  'details',
];

export const getDefaultAdminValues = () => ({
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  phone: '',
  business_role: 0,
});

export const getPermissionIds = permission =>
  permission.permissionIds.filter(Boolean);

export const isPermissionSelected = (selectedPermissions, permission) =>
  getPermissionIds(permission).some(id => selectedPermissions.includes(id));

export const togglePermissionSelection = (
  selectedPermissions,
  permission,
  checked
) => {
  const permissionIds = getPermissionIds(permission);

  if (checked) {
    return Array.from(new Set([...selectedPermissions, ...permissionIds]));
  }

  return selectedPermissions.filter(id => !permissionIds.includes(id));
};

export const getAllPermissionIds = permissionDefinitions =>
  Array.from(
    new Set(
      permissionDefinitions.flatMap(permission => getPermissionIds(permission))
    )
  );

export const areAllPermissionsSelected = (
  selectedPermissions,
  permissionDefinitions
) =>
  permissionDefinitions.every(permission =>
    isPermissionSelected(selectedPermissions, permission)
  );

export const setAllPermissionsSelection = (permissionDefinitions, checked) =>
  checked ? getAllPermissionIds(permissionDefinitions) : [];

export const normalizeAssignableLocations = response => {
  const locations = Array.isArray(response)
    ? response
    : response && (response.locations || response.items || response.data)
    ? response.locations || response.items || response.data
    : [];

  return locations.map(location => ({
    ...location,
    id: Number(location.id),
    label:
      location.full_address ||
      location.name ||
      location.title ||
      location.address ||
      `Lokacija #${location.id}`,
  }));
};

export const normalizeActivityRecords = response => {
  const records = Array.isArray(response)
    ? response
    : response &&
      (response.audit_logs ||
        response.records ||
        response.items ||
        response.logs ||
        response.data)
    ? response.audit_logs ||
      response.records ||
      response.items ||
      response.logs ||
      response.data
    : [];

  return {
    records,
    total: (response && (response.count || response.total)) || records.length,
  };
};

export const mapAdminDetailsToState = admin => ({
  values: {
    first_name: (admin && admin.first_name) || '',
    last_name: (admin && admin.last_name) || '',
    email: (admin && admin.email) || '',
    password: '',
    phone: (admin && admin.phone) || '',
    business_role: Number(admin && admin.business_role) || 0,
  },
  permissions: Array.isArray(admin && admin.permissions)
    ? admin.permissions.map(value => Number(value))
    : [],
  status: admin && (admin.status === 1 || admin.status === true),
  locationIds: Array.isArray(admin && admin.location_ids)
    ? admin.location_ids.map(value => Number(value))
    : [],
  allLocationsSelected: !Array.isArray(admin && admin.location_ids),
  locationEmailNotificationEnabled:
    admin && admin.location_email_notification_enabled === true,
  locationSmsNotificationEnabled:
    admin && admin.location_sms_notification_enabled === true,
});

export const buildAdminPayload = ({
  values,
  permissions,
  locationIds,
  allLocationsSelected,
  locationEmailNotificationEnabled,
  locationSmsNotificationEnabled,
  hasLocationsPermission,
  includeEmail = false,
}) => {
  const payload = {
    first_name: values.first_name,
    last_name: values.last_name,
    phone: values.phone || null,
    business_role: values.business_role || null,
    permissions,
    location_ids:
      hasLocationsPermission && !allLocationsSelected && locationIds.length
        ? locationIds
        : null,
    location_email_notification_enabled:
      hasLocationsPermission && locationEmailNotificationEnabled,
    location_sms_notification_enabled:
      hasLocationsPermission && locationSmsNotificationEnabled,
  };

  if (includeEmail) {
    payload.email = values.email;
  }

  return payload;
};

export const getActivityRecordValue = (record, keys) => {
  for (const key of keys) {
    if (
      record[key] !== undefined &&
      record[key] !== null &&
      record[key] !== ''
    ) {
      return record[key];
    }
  }

  return null;
};

export const getActivityRecordId = record =>
  getActivityRecordValue(record, ACTIVITY_RECORD_ID_KEYS);

export const formatActivityRecordType = record =>
  (() => {
    const value = getActivityRecordValue(record, ACTIVITY_RECORD_TYPE_KEYS);

    if (!value) {
      return '-';
    }

    return (
      String(value)
        .charAt(0)
        .toUpperCase() + String(value).slice(1)
    );
  })();

export const formatActivityRecordTarget = record => {
  const target = getActivityRecordValue(record, ACTIVITY_RECORD_TARGET_KEYS);
  const entityId = getActivityRecordValue(record, ['entity_id']);

  if (!target) {
    return entityId ? `#${entityId}` : '-';
  }

  return entityId ? `${target} (#${entityId})` : target;
};

export const formatActivityRecordDate = record => {
  const timestamp = getActivityRecordValue(record, [
    'created_at',
    'createdAt',
    'date_time',
    'timestamp',
    'happened_at',
  ]);

  if (!timestamp) {
    return '-';
  }

  try {
    return formatLocaleDate(timestamp);
  } catch (e) {
    return String(timestamp);
  }
};

export const formatActivityRecordChanges = record => {
  const summary = getActivityRecordValue(record, ['summary']);

  if (summary) {
    return summary;
  }

  const changes = getActivityRecordValue(record, ACTIVITY_RECORD_CHANGES_KEYS);

  if (Array.isArray(changes)) {
    return changes.join(', ');
  }

  if (changes && typeof changes === 'object') {
    return Object.entries(changes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  }

  return changes || '-';
};

const formatActivityRecordDetailLabel = label =>
  String(label)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const formatActivityRecordDetailValue = value => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'object') {
    return Object.entries(value)
      .map(
        ([key, nestedValue]) =>
          `${formatActivityRecordDetailLabel(
            key
          )}: ${formatActivityRecordDetailValue(nestedValue)}`
      )
      .join(', ');
  }

  return String(value);
};

const buildChangeDescription = (key, beforeValue, afterValue) => {
  const label = formatActivityRecordDetailLabel(key);
  const hasBefore =
    beforeValue !== undefined && beforeValue !== null && beforeValue !== '';
  const hasAfter =
    afterValue !== undefined && afterValue !== null && afterValue !== '';

  if (hasBefore && hasAfter) {
    return `${label}: ${formatActivityRecordDetailValue(
      beforeValue
    )} -> ${formatActivityRecordDetailValue(afterValue)}`;
  }

  if (hasAfter) {
    return `${label}: ${formatActivityRecordDetailValue(afterValue)}`;
  }

  if (hasBefore) {
    return `${label}: ${formatActivityRecordDetailValue(beforeValue)}`;
  }

  return null;
};

export const getActivityRecordDetailsLines = record => {
  const lines = [];
  const summary = getActivityRecordValue(record, ['summary']);
  const before =
    record && typeof record.before === 'object' ? record.before : null;
  const after =
    record && typeof record.after === 'object' ? record.after : null;

  if (summary) {
    lines.push(String(summary));
  }

  if (before || after) {
    const keys = Array.from(
      new Set([...Object.keys(before || {}), ...Object.keys(after || {})])
    );

    keys.forEach(key => {
      const description = buildChangeDescription(
        key,
        before ? before[key] : undefined,
        after ? after[key] : undefined
      );

      if (description) {
        lines.push(description);
      }
    });
  }

  if (lines.length) {
    return lines;
  }

  const changes = getActivityRecordValue(record, ACTIVITY_RECORD_CHANGES_KEYS);

  if (Array.isArray(changes)) {
    return changes.length ? changes.map(change => String(change)) : ['-'];
  }

  if (changes && typeof changes === 'object') {
    const entries = Object.entries(changes).map(
      ([key, value]) =>
        `${formatActivityRecordDetailLabel(
          key
        )}: ${formatActivityRecordDetailValue(value)}`
    );

    return entries.length ? entries : ['-'];
  }

  if (changes) {
    return String(changes)
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
  }

  return ['-'];
};

export const formatAdminDisplayName = admin => {
  if (!admin) {
    return '-';
  }

  const fullName = [admin.first_name, admin.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (!fullName) {
    return admin.email || '-';
  }

  return fullName;
};

export const getActivityRecordAdminPreview = record => ({
  id: getActivityRecordValue(record, ['admin_id']),
  first_name: getActivityRecordValue(record, ['first_name']),
  last_name: getActivityRecordValue(record, ['last_name']),
  email: getActivityRecordValue(record, ['email']),
});

export const getActivityRecordStorageKey = (adminId, recordId) =>
  `admin-activity-record-${adminId}-${recordId}`;
